// NSARA Stores V2 — onglet "Produits" du workspace boutique (sections 10 & 12).
//
// Le catalogue est filtré par shopId CÔTÉ SERVEUR (backend/routes/products.js):
// la recherche et les filtres ci-dessous s'appliquent donc uniquement à une
// liste déjà restreinte à la boutique active — ils ne peuvent jamais faire
// apparaître le produit d'une autre boutique.
//
// Seules les actions réellement supportées par l'API existante sont proposées:
//   Modifier / Voir / Copier le lien / Masquer-Publier / Dupliquer / Supprimer.
// Aucun "brouillon" inventé: le backend expose public_enabled, c'est lui qui
// pilote le statut affiché ("En ligne" / "Masqué").
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Search, MoreHorizontal, Pencil, ExternalLink, Link2, EyeOff, Eye, Copy, Trash2, ImageOff, Loader2 } from 'lucide-react';
import { getShopProducts, updateProduct, deleteProduct, createProduct } from '../../services/apiClient';
import ProductFormDialog from './ProductFormDialog';
import Modal from './Modal';
import { formatMoney, filterProducts, isLowStock, buildPublicProductUrl, PRODUCT_FILTERS } from './storeUtils';

export default function StoreProductsTab({ businessId, shopId, onCatalogChanged, addSignal = 0 }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [menuId, setMenuId] = useState('');
  const [busyId, setBusyId] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const menuRef = useRef(null);

  const load = useCallback(async () => {
    if (!businessId || !shopId) return;
    setLoading(true);
    setError('');
    try {
      const { products: rows } = await getShopProducts({ businessId, shopId, limit: 200 });
      setProducts(rows || []);
    } catch (err) {
      setError(err?.message || 'Impossible de charger les produits.');
    } finally {
      setLoading(false);
    }
  }, [businessId, shopId]);

  // shopId dans les dépendances + réinitialisation explicite: passer d'une
  // boutique à l'autre ne laisse jamais traîner le catalogue précédent.
  useEffect(() => { setProducts([]); setQuery(''); setFilter('all'); setDetail(null); setMenuId(''); load(); }, [load]);

  // Signal "Ajouter un produit" envoyé depuis la barre du workspace: ouvre le
  // formulaire de création, que l'onglet Produits soit déjà actif ou vienne de
  // s'ouvrir (au montage, addSignal est déjà > 0 et l'effet s'applique).
  useEffect(() => {
    if (addSignal > 0) { setEditing(null); setFormOpen(true); }
  }, [addSignal]);

  useEffect(() => {
    if (!menuId) return undefined;
    const onPointerDown = (event) => { if (!menuRef.current?.contains(event.target)) setMenuId(''); };
    const onKeyDown = (event) => { if (event.key === 'Escape') setMenuId(''); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => { document.removeEventListener('mousedown', onPointerDown); document.removeEventListener('keydown', onKeyDown); };
  }, [menuId]);

  const flash = (message) => { setNotice(message); window.setTimeout(() => setNotice(''), 2600); };

  const copyLink = async (product) => {
    setMenuId('');
    try {
      await navigator.clipboard.writeText(buildPublicProductUrl(product.public_id));
      flash('Lien copié');
    } catch (err) {
      setError('Copie impossible sur ce navigateur.');
    }
  };

  const run = async (product, action) => {
    setMenuId('');
    setBusyId(product.id);
    setError('');
    try {
      await action(product);
      await load();
      onCatalogChanged?.();
    } catch (err) {
      setError(err?.message || 'Action impossible.');
    } finally {
      setBusyId('');
    }
  };

  const togglePublic = (product) => run(product, async (row) => {
    await updateProduct(row.id, { publicEnabled: !row.public_enabled });
    flash(row.public_enabled ? 'Produit masqué.' : 'Produit publié.');
  });

  const duplicate = (product) => run(product, async (row) => {
    await createProduct({
      businessId,
      shopId,
      name: `${row.name} (copie)`,
      description: row.description || '',
      category: row.category || '',
      sellingPrice: Number(row.selling_price),
      stockQuantity: 0,
      lowStockThreshold: Number(row.low_stock_threshold || 0),
      photoUrl: row.photo_url || '',
      publicEnabled: false
    });
    flash('Produit dupliqué (masqué, stock à définir).');
  });

  const remove = (product) => run(product, async (row) => {
    await deleteProduct(row.id);
    flash('Produit retiré du catalogue.');
  });

  const visible = filterProducts(products, filter, query);

  return (
    <div className="stores-section">
      <div className="stores-section-head">
        <div className="stores-head-text">
          <h2 className="stores-section-title">Produits</h2>
          <p className="stores-section-sub">Gérez ce que vous vendez dans cette boutique.</p>
        </div>
        <button type="button" className="stores-btn stores-btn--primary" onClick={() => { setEditing(null); setFormOpen(true); }}>
          <Plus size={17} aria-hidden="true" /> Ajouter un produit
        </button>
      </div>

      {error && <p className="stores-alert stores-alert--error" role="alert">{error}</p>}
      {notice && <p className="stores-alert stores-alert--success" role="status">{notice}</p>}

      {products.length > 0 && (
        <div className="stores-toolbar">
          <div className="stores-chips" role="group" aria-label="Filtrer les produits">
            {PRODUCT_FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={filter === item.id ? 'stores-chip is-active' : 'stores-chip'}
                aria-pressed={filter === item.id}
                onClick={() => setFilter(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="stores-search">
            <Search size={16} aria-hidden="true" className="stores-search-icon" />
            <input
              className="stores-input"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Rechercher un produit"
              aria-label="Rechercher un produit"
            />
          </div>
        </div>
      )}

      {loading && (
        <div className="stores-product-list" aria-busy="true" aria-label="Chargement des produits">
          {[0, 1, 2].map((key) => <div key={key} className="stores-skeleton-row" style={{ height: 76 }} />)}
        </div>
      )}

      {!loading && products.length === 0 && (
        <div className="stores-empty">
          <span className="stores-empty-icon" aria-hidden="true"><ImageOff size={22} /></span>
          <h3>Aucun produit dans cette boutique</h3>
          <p>Ajoutez votre premier produit : nom, prix, stock. La photo est facultative.</p>
          <button type="button" className="stores-btn stores-btn--primary" onClick={() => { setEditing(null); setFormOpen(true); }}>
            <Plus size={17} aria-hidden="true" /> Ajouter un produit
          </button>
        </div>
      )}

      {!loading && products.length > 0 && visible.length === 0 && (
        <div className="stores-empty">
          <h3>Aucun produit ne correspond</h3>
          <p>Essayez un autre mot-clé ou revenez au filtre « Tous ».</p>
          <button type="button" className="stores-btn stores-btn--outline" onClick={() => { setQuery(''); setFilter('all'); }}>Réinitialiser les filtres</button>
        </div>
      )}

      {!loading && visible.length > 0 && (
        <div className="stores-product-list">
          {visible.map((product) => {
            const low = isLowStock(product);
            const busy = busyId === product.id;
            return (
              <article className="stores-product-row" key={product.id}>
                <span className="stores-product-thumb" aria-hidden="true">
                  {product.photo_url ? <img src={product.photo_url} alt="" loading="lazy" /> : <ImageOff size={18} />}
                </span>
                <div className="stores-product-body">
                  <h3 className="stores-product-name">{product.name}</h3>
                  <p className="stores-product-meta">
                    <span className="stores-product-price">{formatMoney(product.selling_price)}</span>
                    <span>Stock : {Number(product.stock_quantity)}</span>
                    {product.public_enabled
                      ? <span className="stores-badge stores-badge--live">En ligne</span>
                      : <span className="stores-badge stores-badge--hidden">Masqué</span>}
                    {low && <span className="stores-badge stores-badge--low stores-badge--plain">Stock faible</span>}
                  </p>
                </div>
                <div className="stores-product-side">
                  <button type="button" className="stores-icon-btn" aria-label={`Modifier ${product.name}`} disabled={busy} onClick={() => { setEditing(product); setFormOpen(true); }}>
                    <Pencil size={16} aria-hidden="true" />
                  </button>
                  <div className="stores-menu-anchor">
                    <button
                      type="button"
                      className="stores-icon-btn"
                      aria-label={`Autres actions pour ${product.name}`}
                      aria-haspopup="menu"
                      aria-expanded={menuId === product.id}
                      disabled={busy}
                      onClick={() => setMenuId((current) => (current === product.id ? '' : product.id))}
                    >
                      {busy ? <Loader2 size={16} aria-hidden="true" className="stores-spin" /> : <MoreHorizontal size={16} aria-hidden="true" />}
                    </button>
                    {menuId === product.id && (
                      <div className="stores-menu" role="menu" ref={menuRef}>
                        <button type="button" role="menuitem" onClick={() => { setMenuId(''); setDetail(product); }}>Voir</button>
                        <button type="button" role="menuitem" disabled={!product.public_enabled} onClick={() => copyLink(product)}>
                          <Link2 size={15} aria-hidden="true" /> Copier le lien
                        </button>
                        <button type="button" role="menuitem" onClick={() => togglePublic(product)}>
                          {product.public_enabled ? <EyeOff size={15} aria-hidden="true" /> : <Eye size={15} aria-hidden="true" />}
                          {product.public_enabled ? 'Masquer' : 'Publier'}
                        </button>
                        <button type="button" role="menuitem" onClick={() => duplicate(product)}>
                          <Copy size={15} aria-hidden="true" /> Dupliquer
                        </button>
                        <button type="button" role="menuitem" className="is-danger" onClick={() => remove(product)}>
                          <Trash2 size={15} aria-hidden="true" /> Supprimer
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <ProductFormDialog
        open={formOpen}
        businessId={businessId}
        shopId={shopId}
        product={editing}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSaved={async () => {
          const wasEditing = Boolean(editing);
          setFormOpen(false);
          setEditing(null);
          flash(wasEditing ? 'Produit enregistré.' : 'Produit ajouté.');
          await load();
          onCatalogChanged?.();
        }}
      />

      <Modal open={Boolean(detail)} onClose={() => setDetail(null)} labelledBy="product-detail-title" size="md">
        {detail && (
          <div className="stores-dialog">
            <div className="stores-dialog-head">
              <span className="stores-logo-preview" aria-hidden="true">
                {detail.photo_url ? <img src={detail.photo_url} alt="" /> : <ImageOff size={20} />}
              </span>
              <div>
                <h2 id="product-detail-title" className="stores-dialog-title">{detail.name}</h2>
                <p className="stores-dialog-subtitle">
                  {detail.public_enabled ? 'Visible sur la vitrine publique' : 'Masqué sur la vitrine publique'}
                </p>
              </div>
            </div>

            <div className="stores-detail-grid">
              <div>
                <div className="stores-detail-item"><span>Prix de vente</span><strong>{formatMoney(detail.selling_price)}</strong></div>
                <div className="stores-detail-item"><span>Stock disponible</span><strong>{Number(detail.stock_quantity)}</strong></div>
              </div>
              <div>
                <div className="stores-detail-item"><span>Seuil d’alerte</span><strong>{Number(detail.low_stock_threshold || 0)}</strong></div>
                <div className="stores-detail-item"><span>Catégorie</span><strong>{detail.category || '—'}</strong></div>
              </div>
            </div>

            {detail.description && (
              <div className="stores-detail-block">
                <p className="stores-detail-label">Description</p>
                <p className="stores-detail-value">{detail.description}</p>
              </div>
            )}

            <div className="stores-dialog-actions">
              <button type="button" className="stores-btn stores-btn--outline" onClick={() => { const row = detail; setDetail(null); setEditing(row); setFormOpen(true); }}>
                <Pencil size={15} aria-hidden="true" /> Modifier
              </button>
              <button type="button" className="stores-btn stores-btn--outline" disabled={!detail.public_enabled} onClick={() => copyLink(detail)}>
                <Link2 size={15} aria-hidden="true" /> Copier le lien
              </button>
              <a
                className="stores-btn stores-btn--primary"
                href={buildPublicProductUrl(detail.public_id)}
                target="_blank"
                rel="noreferrer noopener"
                aria-disabled={!detail.public_enabled}
                onClick={(event) => { if (!detail.public_enabled) event.preventDefault(); }}
              >
                <ExternalLink size={15} aria-hidden="true" /> Voir la page publique
              </a>
            </div>
            {!detail.public_enabled && <p className="stores-hint">Publiez ce produit pour rendre sa page publique accessible.</p>}
          </div>
        )}
      </Modal>
    </div>
  );
}
